import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { staleGuard, dedupeInFlight } from "src/utils/staleGuard";
import journal from "src/utils/journal/journal";
import { currentBusinessDayBounds, selectBusinessDayStartHour } from "src/utils/businessDay";

const initialState = {
  isLoading: false,
  error: false,
  pedidos: [],
  sessions: [],
  // 2026-10-26 · Cifras del arqueo calculadas por el servidor con la regla común
  // (impreso, email, WhatsApp, vista). null = desactualizadas (TodaySummary recalcula).
  resumen: null,
  filters: [],
  actions: [],
  balance: [],
  balanceTarjeta: [],
  arqueo: null,
  todayArqueo: null,
  lastArqueoDay: null,
  openArqueoDialog: null,
  arqueos: [],
};

const slice = createSlice({
  name: "caja",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getCajaSuccess(state, action) {
      state.pedidos = action.payload.pedidos;
      state.arqueo = action.payload.caja;
      state.balance = action.payload.balance;
      state.actions = action.payload.actions;
      state.sessions = action.payload.sessions;
      state.resumen = action.payload.resumen ?? null;
      state.isLoading = false;
    },
    startArqueoSuccess(state, action) {
      state.isLoading = false;
      state.todayArqueo = action.payload;
      state.arqueo = action.payload;

      state.arqueos = state.arqueos.map((a) => {
        if (a.id === action.payload.id) {
          return action.payload;
        }
        return a;
      });
    },
    getTodayCajaSuccess(state, action) {
      state.todayArqueo = action.payload.arqueo;
      state.lastArqueoDay = action.payload.last;

      if (
        action.payload.arqueo &&
        (!action.payload.arqueo.isOpen || action.payload.arqueo.isClose)
      )
        state.openArqueoDialog = true;
      else state.openArqueoDialog = false;

      state.isLoading = false;
    },
    getTodayCajaSuccessWithoutDialog(state, action) {
      state.todayArqueo = action.payload.arqueo;
      state.lastArqueoDay = action.payload.last;
      state.isLoading = false;
    },
    openArqueoDialogSuccess(state, action) {
      state.openArqueoDialog = action.payload;
    },
    cleanCaja(state) {
      state.pedidos = [];
      state.arqueo = null;
      state.balance = [];
      state.actions = [];
      state.sessions = [];
      state.resumen = null;
      state.isLoading = false;
      state.filters = [];
      state.balanceTarjeta = [];
      state.arqueos = [];
    },
    setInitialMoney(state, action) {
      state.arqueo = {
        ...state.arqueo,
        initialMoney: Number(action.payload),
      };
    },
    setAsPaySuccess(state, action) {
      // Las cifras del servidor ya no reflejan esta sesión: recálculo local.
      state.resumen = null;
      state.sessions = state.sessions.map((s) => {
        if (s.id === action.payload.id) {
          return action.payload;
        }
        return s;
      });
    },
    addActionCajaSuccess(state, action) {
      state.actions = [...state.actions, action.payload];
    },
    deleteSuccess(state, action) {
      state.pedidos = state.pedidos.filter((s) => s.id !== action.payload);
    },
    deleteSessionSuccess(state, action) {
      state.resumen = null;
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
    },
    getArqueosSuccess(state, action) {
      state.arqueos = action.payload;
    },
    deleteActionSuccess(state, action) {
      state.actions = state.actions.filter((a) => a.id !== action.payload);
    },
    makeArqueoSuccess(state, action) {
      state.arqueo = action.payload;
      state.arqueos = state.arqueos.map((a) => {
        if (a.id === action.payload.id) {
          return action.payload;
        }
        return a;
      });
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  deleteSuccess,
  getCajaSuccess,
  addActionCajaSuccess,
  deleteActionSuccess,
  deleteSessionSuccess,
  makeArqueoSuccess,
  cleanCaja,
  openArqueoDialogSuccess,
  startArqueoSuccess,
  getTodayCajaSuccessWithoutDialog,
} = slice.actions;

// ----------------------------------------------------------------------

// ── Caja del arqueo (`data/caja`) ───────────────────────────────────
// 2026-10-04 · Una sola petición por arqueo a la vez y gana la última:
//   · Dos llamadas iguales en vuelo (entrar en Caja mientras otra pantalla
//     la pide, doble click en el menú «¿Cuándo?») comparten la misma
//     petición en vez de repetirla.
//   · Si el usuario cambia de arqueo antes de que llegue la respuesta del
//     anterior, la respuesta vieja se descarta (antes podía llegar tarde y
//     pintar el arqueo equivocado).
let cajaRequestSeq = 0;

const cajaBody = (filters) => ({
  idMesa: filters.idMesa,
  startDate: filters.startDate,
  endDate: filters.endDate,
  onlyCash: filters.onlyCash,
  initialMoney: filters.initialMoney,
  idArqueo: filters.idArqueo,
});

const fetchCaja = (filters) => {
  const body = cajaBody(filters);
  return dedupeInFlight(`caja/data/${JSON.stringify(body)}`, () =>
    axios
      .post(`data/caja`, JSON.stringify(body), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      })
      .then((response) => response.data)
  );
};

// 2026-10-27 · Últimos filtros cargados: tras un cobro o un borrado desde la propia
// caja se vuelven a pedir las cifras al servidor (mismas que el impreso, el email y
// WhatsApp); el recálculo local solo cubre el rato hasta que llegan.
let lastCajaFilters = null;

const loadCaja = (dispatch, filters) => {
  lastCajaFilters = filters;
  const seq = ++cajaRequestSeq;
  return fetchCaja(filters).then(
    (data) => {
      if (seq === cajaRequestSeq) dispatch(slice.actions.getCajaSuccess(data));
    },
    (error) => {
      if (seq === cajaRequestSeq) dispatch(slice.actions.hasError(error));
    }
  );
};

export function refreshCaja() {
  return async (dispatch) => {
    if (lastCajaFilters) await loadCaja(dispatch, lastCajaFilters);
  };
}

export function getAllCaja(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    await loadCaja(dispatch, filters);
  };
}

export function getAllCajaAndArqueos(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    // La caja se pinta en cuanto llega su respuesta; la lista de arqueos
    // (solo alimenta el menú «¿Cuándo?») ya no la retiene. Misma clave y
    // forma que `getArqueos`: si el arranque ya la está pidiendo, se
    // comparte esa petición (que es la que guarda el resultado).
    const arqueos = dedupeInFlight("caja/arqueos", async () => {
      const response = await axios.get(`data/arqueos`);
      dispatch(slice.actions.getArqueosSuccess(response.data));
    }).catch((error) => dispatch(slice.actions.hasError(error)));

    await Promise.all([loadCaja(dispatch, filters), arqueos]);
  };
}

export function openBox() {
  return async (dispatch) => {
    journal.track("drawer.click");
    try {
      // El backend resuelve el restaurante desde el JWT si el path viene
      // vacío. localStorage.userId puede quedar null tras un cambio de
      // sesión, así que preferimos omitir el segmento antes que mandar
      // la string literal "null" (que se veía como `caja/open/null`).
      var user = window.localStorage.getItem("userId");
      var url = user && user !== "null" ? `caja/open/${user}` : "caja/open";
      await axios.post(url);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCurrentCaja(openDialog = true, { force = false } = {}) {
  return async (dispatch, getState) => {
    // El arqueo del día es estable durante bastante tiempo (abrir /
    // cerrar caja son eventos puntuales). SignalR ya notifica los
    // cambios importantes; con staleGuard evitamos re-fetchearlo cada
    // vez que el TPV remonta (navegar mesa → tpv → mesa → tpv…).
    if (!force && staleGuard("caja/current")) return;
    try {
      // Jornada en curso según la hora de inicio de jornada del restaurante.
      const { start: today, end: tomorrow } = currentBusinessDayBounds(
        selectBusinessDayStartHour(getState?.()),
      );

      const response = await axios.post(
        `data/getCajaAndLast`,
        JSON.stringify({
          idMesa: null,
          startDate: today,
          endDate: tomorrow,
          onlyCash: false,
          initialMoney: 0,
          idArqueo: null,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );

      if (openDialog)
        dispatch(slice.actions.getTodayCajaSuccess(response.data));
      else
        dispatch(slice.actions.getTodayCajaSuccessWithoutDialog(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllCajaFranquicia(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(
        `data/caja/franquicia`,
        JSON.stringify({
          idRestaurante: filters.idRestaurante,
          idMesa: null,
          startDate: filters.startDate,
          endDate: filters.endDate,
          onlyCash: filters.onlyCash,
          initialMoney: filters.initialMoney,
          idArqueo: filters.idArqueo,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.getCajaSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function StartArqueo(arqueo) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`data/startArqueo/${arqueo}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.startArqueoSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getArqueos(id) {
  return async (dispatch) => {
    // 2026-09-11 · dedupeInFlight + staleGuard: authJwt.getInitialize() +
    // caja.getAllCajaAndArqueos() + AlertsCenter dispatchan getArqueos()
    // en el mismo tick al bootstrap. HAR mostraba 3 GETs a /data/arqueos
    // en el arranque. Con dedupe → 1 sola. staleGuard TTL 30s evita
    // re-fetches en navegación rápida entre TPV / Caja.
    if (staleGuard("caja/arqueos", 30_000)) return;
    return dedupeInFlight("caja/arqueos", async () => {
      var response = await axios.get(`data/arqueos`);
      dispatch(slice.actions.getArqueosSuccess(response.data));
    });
  };
}

export function initialMoney(arqueo) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setInitialMoney(arqueo.initialMoney));
      // `billetes` opcional — cuando el camarero usa el modal "Contar por
      // monedas" del OpeningCountDialog, viaja como List<{value, unidades}>
      // y el backend lo persiste en `Arqueo.OpeningBilletes` (JSON). Si el
      // modal simple no lo manda, el backend NO sobrescribe con null (guard
      // en SetArqueoInicial). `idEmployee` también opcional para trazar
      // quién contó.
      await axios.post(
        `data/set/caja`,
        JSON.stringify({
          idArqueo: arqueo.idArqueo,
          initialMoney: arqueo.initialMoney,
          motivo: arqueo.motivo,
          billetes: arqueo.billetes || null,
          idEmployee: arqueo.idEmployee || 0,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addCaja(arqueo) {
  return async (dispatch) => {
    try {
      var response = await axios.post(
        `data/AddCaja`,
        JSON.stringify({
          idArqueo: arqueo.idArqueo,
          initialMoney: arqueo.initialMoney,
          motivo: arqueo.motivo,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(addActionCajaSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteAction(action) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.deleteActionSuccess(action));
      await axios.delete(`data/DeleteCaja/${action}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function SetAsPay(id, isEfectivo) {
  return async (dispatch) => {
    try {
      await axios
        .get(`data/caja/setaspay/${id}/${isEfectivo}`)
        .then((res) => {
          dispatch(slice.actions.setAsPaySuccess(res.data));
          dispatch(refreshCaja());
        })
        .catch((err) => {
          if (process.env.NODE_ENV != "production") console.log(err);
        });
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function Delete(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.deleteSuccess(id));
      // await axios.delete(`data/caja/delete/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV != "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
